import { EquipmentType } from './equipment-types'
import { FundingSchedule } from './loan-application'

export type TestProjectFieldType =
  | 'boolean'
  | 'date'
  | 'enum'
  | 'json'
  | 'number'
  | 'string'
  | 'timestamp'

export type TestProjectFieldSource = 'airtable' | 'derived' | 'model'

export interface TestProjectFieldDefinition {
  field: string
  label: string
  type: TestProjectFieldType
  source: TestProjectFieldSource
  airtableField?: string
  editable: boolean
  workflowCritical?: boolean
  options?: string[]
}

export interface TestProjectField extends TestProjectFieldDefinition {
  value?: unknown
}

export interface TestProjectFieldsResponse {
  fields: TestProjectField[]
}

export interface TestProjectFieldUpdate {
  field: string
  value?: unknown
  clear?: boolean
}

export interface TestProjectFieldUpdateResponse extends TestProjectFieldsResponse {
  project: Record<string, unknown>
  appliedChange: {
    field: string
    value?: unknown
    cleared: boolean
  }
}

const airtableString = (
  field: string,
  label: string,
  airtableField = label,
  workflowCritical = false,
): TestProjectFieldDefinition => ({
  field,
  label,
  airtableField,
  type: 'string',
  source: 'airtable',
  editable: true,
  workflowCritical,
})

const airtableDate = (
  field: string,
  label: string,
  airtableField = label,
  workflowCritical = false,
): TestProjectFieldDefinition => ({
  ...airtableString(field, label, airtableField, workflowCritical),
  type: 'date',
})

const airtableTimestamp = (
  field: string,
  label: string,
  airtableField = label,
  workflowCritical = false,
): TestProjectFieldDefinition => ({
  ...airtableString(field, label, airtableField, workflowCritical),
  type: 'timestamp',
})

const airtableBoolean = (
  field: string,
  label: string,
  airtableField = label,
  workflowCritical = false,
): TestProjectFieldDefinition => ({
  field,
  label,
  airtableField,
  type: 'boolean',
  source: 'airtable',
  editable: true,
  workflowCritical,
})

const airtableCommentFields: TestProjectFieldDefinition[] = [
  ['contractNotes', 'Contract Notes'],
  ['loanDocumentsSentNotes', 'Loan Documents Sent Notes'],
  ['loanDocumentsSignedNotes', 'Loan Documents Signed Notes'],
  ['initialBankReviewNotes', 'Initial Bank Review Notes'],
  ['homeOwnershipVerificationNotes', 'Home Ownership Verification Notes'],
  ['bankFinalReviewNotes', 'Bank Final Review Notes'],
  ['roofPermittingComments', 'Roof Permitting Comments'],
  ['passedRoofInspectionComments', 'Passed Roof Inspection Comments'],
  ['roofLienWaiverFormComments', 'Roof Lien Waiver Form Comments'],
  ['roofFundReleaseRequestComments', 'Roof Fund Release Request Comments'],
  ['roofFundingDisbursedComments', 'Roof Funding Disbursed Comments'],
  ['constructionPermitComments', 'Construction Permit Comments'],
  ['engineeringPlansComments', 'Engineering Plans Comments'],
  ['initialFundReleaseRequestComments', 'Initial Fund Release Request Comments'],
  ['initialFundingDisbursedComments', 'Initial Funding Disbursed Comments'],
  ['proofOfPassedInspectionComments', 'Proof Of Passed Inspection Comments'],
  ['proofOfPermissionToOperateComments', 'Proof Of Permission To Operate Comments'],
  ['contractorLienWaiverFormComments', 'Contractor Lien Waiver Form Comments'],
  ['finalFundingReleaseRequestComments', 'Final Funding Release Request Comments'],
  ['finalFundingDisbursedComments', 'Final Funding Disbursed Comments'],
  ['fullFundingReleaseRequestComments', 'Full Funding Release Request Comments'],
].map(([field, label]) => airtableString(field, label))

export const TEST_PROJECT_FIELD_DEFINITIONS: TestProjectFieldDefinition[] = [
  {
    field: 'installersStatus',
    label: 'Installer Status',
    airtableField: 'Installers Status',
    type: 'string',
    source: 'airtable',
    editable: true,
    workflowCritical: true,
  },
  {
    field: 'fundingSchedule',
    label: 'Funding Schedule',
    airtableField: 'Funding Schedule',
    type: 'enum',
    source: 'airtable',
    editable: true,
    workflowCritical: true,
    options: Object.values(FundingSchedule),
  },
  {
    field: 'equipmentType',
    label: 'Equipment Type',
    airtableField: 'Equipment Type',
    type: 'enum',
    source: 'airtable',
    editable: true,
    workflowCritical: true,
    options: Object.values(EquipmentType),
  },
  {
    field: 'projectType',
    label: 'Project Type',
    airtableField: 'Application Type',
    type: 'enum',
    source: 'airtable',
    editable: true,
    options: ['residential', 'commercial', 'non-profit'],
  },
  {
    field: 'interest',
    label: 'Interest Rate',
    type: 'number',
    source: 'model',
    editable: true,
  },
  {
    field: 'interestOnlyMonths',
    label: 'Interest-only Months',
    type: 'number',
    source: 'model',
    editable: true,
  },
  {
    field: 'loanDurationMonths',
    label: 'Loan Duration Months',
    type: 'number',
    source: 'model',
    editable: true,
  },
  {
    field: 'solarCost',
    label: 'Solar Cost',
    type: 'number',
    source: 'model',
    editable: true,
    workflowCritical: true,
  },
  {
    field: 'finalFundReleaseAmount',
    label: 'Final Fund Release Amount',
    airtableField: 'Final Fund Release Amount',
    type: 'number',
    source: 'airtable',
    editable: true,
    workflowCritical: true,
  },
  {
    field: 'systemSizeKW',
    label: 'System Size KW',
    airtableField: 'System Size KW',
    type: 'number',
    source: 'airtable',
    editable: true,
  },
  {
    field: 'storageCapacity',
    label: 'Storage Capacity',
    type: 'number',
    source: 'model',
    editable: true,
  },
  {
    field: 'storageQty',
    label: 'Storage Quantity',
    type: 'number',
    source: 'model',
    editable: true,
  },
  {
    field: 'teslaPowerwallQty',
    label: 'Tesla Powerwall Quantity',
    type: 'number',
    source: 'model',
    editable: true,
  },
  {
    field: 'ppWForPV',
    label: 'Price Per Watt For PV',
    type: 'number',
    source: 'model',
    editable: true,
  },
  {
    field: 'ppWForStorage',
    label: 'Price Per Watt For Storage',
    type: 'number',
    source: 'model',
    editable: true,
  },
  airtableDate('applicationReceived', 'Application Received'),
  airtableDate('loanDocsIssuedDate', 'Loan Documents Issued Date', 'Date Loan Docs Issued'),
  airtableDate('loanDocsSignedDate', 'Loan Documents Signed Date'),
  airtableDate('docPrepQueueDate', 'Doc Prep Queue Date'),
  airtableDate('pendingBookingDate', 'Final Bank Review Date', 'Pending Booking Date'),
  airtableDate('nTPGiven', 'NTP Given', 'NTP Given', true),
  airtableDate('propertyAppraiserReviewed', 'Property Appraiser Reviewed'),
  airtableDate('roofFundingDate', 'Roof Funding Date', 'Roof Funding Date', true),
  airtableDate('partialFundingDate', 'Initial Funding Date', 'Partial Funding Date', true),
  airtableDate('fullFundingDate', 'Final Funding Date', 'Full Funding Date', true),
  airtableTimestamp(
    'lenderInspectionRequestedTimestamp',
    'Lender Inspection Requested',
    'Lender Inspection Requested',
    true,
  ),
  airtableBoolean(
    'lenderInspectionRequiresBorrowerInvolvement',
    'Lender Inspection Requires Borrower Involvement',
    'Lender Inspection Requires Borrower Involvement',
    true,
  ),
  airtableDate(
    'lenderInspectionVerifiedDate',
    'Lender Inspection Verified Date',
    'Lender Inspection Verified Date',
    true,
  ),
  airtableString('fundingBlock', 'Funding Block', 'Funding Block', true),
  ...airtableCommentFields,
  {
    field: 'loanAmount',
    label: 'Calculated Loan Amount',
    type: 'number',
    source: 'derived',
    editable: false,
  },
  {
    field: 'systemCost',
    label: 'Calculated System Cost',
    type: 'number',
    source: 'derived',
    editable: false,
  },
  {
    field: 'fundReleaseAmounts',
    label: 'Calculated Fund Release Amounts',
    type: 'json',
    source: 'derived',
    editable: false,
  },
]

